<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Verwaltung</p>
        <h1 class="page-title">Benutzerkonten</h1>
      </div>
      <div class="d-flex align-center" style="gap: 10px">
        <button class="icon-btn-outlined" :disabled="loading" @click="loadEmployees" title="Aktualisieren">
          <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="#0a1628" />
          <v-icon v-else size="16">mdi-refresh</v-icon>
        </button>
        <button v-if="$can('employees.create')" class="primary-btn" @click="$router.push('/employee/create')">
          <v-icon size="16">mdi-plus</v-icon>
          <span>Benutzer anlegen</span>
        </button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar mb-6">
      <div class="result-count">
        <template v-if="!loading">
          {{ employees.length }}
          {{ employees.length === 1 ? 'Benutzer' : 'Benutzer' }}
        </template>
      </div>

      <div class="view-toggle">
        <button class="view-btn" :class="{ active: viewMode === 'grid' }" title="Grid" @click="setViewMode('grid')">
          <v-icon size="16">mdi-view-grid-outline</v-icon>
        </button>
        <button class="view-btn" :class="{ active: viewMode === 'list' }" title="Liste" @click="setViewMode('list')">
          <v-icon size="16">mdi-format-list-bulleted</v-icon>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div v-for="i in 6" :key="`skel-${i}`" :class="viewMode === 'grid' ? 'skel-card-grid' : 'skel-card-list'"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="employees.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-account-group-outline</v-icon>
      <h3 class="empty-title">Keine Benutzer vorhanden</h3>
      <p class="empty-desc">Erstelle den ersten Benutzer, um loszulegen.</p>
      <button v-if="$can('employees.create')" class="primary-btn mt-4" @click="$router.push('/employee/create')">
        <v-icon size="16">mdi-plus</v-icon>
        Ersten Benutzer erstellen
      </button>
    </div>

    <!-- Grid -->
    <div v-else-if="viewMode === 'grid'" class="grid-emp">
      <div
        v-for="employee in employees"
        :key="employee.id"
        class="emp-card"
        @click="$router.push(`/employee/${employee.id}`)"
      >
        <div class="emp-avatar-wrap">
          <v-avatar size="88" class="emp-avatar">
            <v-img
              :src="`/api/employees/${employee.id}/image`"
              :alt="`${employee.firstname} ${employee.lastname}`"
              cover
            >
              <template #error>
                <div class="emp-avatar-fallback">{{ initials(employee) }}</div>
              </template>
            </v-img>
          </v-avatar>
        </div>

        <div class="emp-body">
          <div class="emp-name">{{ employee.firstname }} {{ employee.lastname }}</div>
          <div class="emp-email">{{ employee.email || '—' }}</div>
          <div v-if="employee.position" class="emp-position">
            <v-icon size="11" color="#888">mdi-briefcase-outline</v-icon>
            {{ employee.position }}
          </div>

          <div class="emp-badges">
            <span v-if="employee.isTechnicalReviewer" class="badge-role badge-tech">
              <v-icon size="10">mdi-code-tags</v-icon>Technisch
            </span>
            <span v-if="employee.isVisualReviewer" class="badge-role badge-visual">
              <v-icon size="10">mdi-eye-outline</v-icon>Optisch
            </span>
          </div>
        </div>

        <div class="emp-actions">
          <button v-if="$can('employees.edit')" class="action-btn" title="Bearbeiten" @click.stop="$router.push(`/employee/edit/${employee.id}`)">
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </button>
          <button v-if="$can('employees.delete')" class="action-btn action-danger" title="Löschen" @click.stop="deleteEmployee(employee)">
            <v-icon size="16">mdi-delete-outline</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- List -->
    <div v-else class="list-wrap">
      <div class="list-header">
        <div class="list-col-ava"></div>
        <div>Name</div>
        <div>E-Mail</div>
        <div>Position</div>
        <div>Rolle</div>
        <div class="list-col-actions">Aktionen</div>
      </div>
      <div
        v-for="employee in employees"
        :key="employee.id"
        class="list-row"
        @click="$router.push(`/employee/${employee.id}`)"
      >
        <div class="list-col-ava">
          <v-avatar size="36" class="emp-avatar">
            <v-img :src="`/api/employees/${employee.id}/image`" cover>
              <template #error>
                <div class="emp-avatar-fallback list-initials">{{ initials(employee) }}</div>
              </template>
            </v-img>
          </v-avatar>
        </div>
        <div class="list-name">{{ employee.firstname }} {{ employee.lastname }}</div>
        <div class="list-muted">{{ employee.email || '—' }}</div>
        <div class="list-muted">{{ employee.position || '—' }}</div>
        <div class="list-roles">
          <span v-if="employee.isTechnicalReviewer" class="badge-role badge-tech">
            <v-icon size="10">mdi-code-tags</v-icon>Tech
          </span>
          <span v-if="employee.isVisualReviewer" class="badge-role badge-visual">
            <v-icon size="10">mdi-eye-outline</v-icon>Optisch
          </span>
          <span v-if="!employee.isTechnicalReviewer && !employee.isVisualReviewer" class="list-muted">—</span>
        </div>
        <div class="list-col-actions">
          <button class="action-btn" title="Bearbeiten" @click.stop="$router.push(`/employee/edit/${employee.id}`)">
            <v-icon size="15">mdi-pencil-outline</v-icon>
          </button>
          <button class="action-btn action-danger" title="Löschen" @click.stop="deleteEmployee(employee)">
            <v-icon size="15">mdi-delete-outline</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor === 'success' ? '#2E7D32' : '#D32F2F'"
      timeout="3000"
      location="top"
    >
      <div class="d-flex align-center" style="gap: 8px">
        <v-icon size="18" color="white">{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Employees',
  data() {
    return {
      employees: [],
      loading: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      viewMode: localStorage.getItem('employees.viewMode') === 'list' ? 'list' : 'grid',
    };
  },

  async mounted() { await this.loadEmployees(); },

  methods: {
    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem('employees.viewMode', mode);
    },
    initials(emp) {
      if (!emp) return '?';
      return ((emp.firstname?.charAt(0) || '') + (emp.lastname?.charAt(0) || '')).toUpperCase();
    },

    async loadEmployees() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/employees/get');
        this.employees = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Fehler beim Laden!', e);
        this.showSnackbar('Fehler beim Laden der Benutzer', 'error');
      } finally {
        this.loading = false;
      }
    },

    async deleteEmployee(employee) {
      if (!confirm(`Möchten Sie den Benutzer "${employee.firstname} ${employee.lastname}" wirklich löschen?`)) return;
      try {
        await axios.post('/api/employees/delete', { id: employee.id });
        this.showSnackbar('Benutzer erfolgreich gelöscht');
        await this.loadEmployees();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        const message = error.response?.data?.message || 'Fehler beim Löschen des Benutzers';
        this.showSnackbar(message, 'error');
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Buttons */
.primary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.primary-btn:hover:not(:disabled) { background: #1d3457; }

.icon-btn-outlined {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  cursor: pointer; color: #0a1628; transition: border-color 0.15s;
}
.icon-btn-outlined:hover:not(:disabled) { border-color: #0a1628; }
.icon-btn-outlined:disabled { opacity: 0.5; cursor: not-allowed; }

.action-btn {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: 8px;
  cursor: pointer; color: #666; transition: all 0.15s;
}
.action-btn:hover { background: #f0f2f5; color: #0a1628; }
.action-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* Toolbar */
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.result-count { font-size: 12px; color: #888; font-weight: 500; }

.view-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px; background: white; border: 1px solid #eceef2; border-radius: 10px; height: 40px;
}
.view-btn {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 7px; cursor: pointer; color: #888;
  transition: all 0.15s;
}
.view-btn:hover:not(.active) { background: #f8f9fb; color: #0a1628; }
.view-btn.active { background: #0a1628; color: white; }
.view-btn.active :deep(.v-icon) { color: white !important; }

/* Grid */
.grid-emp {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}
.emp-card {
  background: white; border: 1px solid #eceef2; border-radius: 14px;
  padding: 20px; cursor: pointer; transition: all 0.15s;
  display: flex; flex-direction: column; align-items: center;
  position: relative;
}
.emp-card:hover {
  border-color: #0a1628; transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(10,22,40,0.06);
}

.emp-avatar-wrap {
  padding: 6px; background: #f3f4f6; border-radius: 50%;
  margin-bottom: 12px;
}
.emp-avatar { background: #e8eaed !important; }
.emp-avatar-fallback {
  width: 100%; height: 100%; display: flex;
  align-items: center; justify-content: center;
  background: linear-gradient(135deg, #5B5BD6, #7C4DFF);
  color: white; font-size: 22px; font-weight: 700; letter-spacing: 0.5px;
}
.list-initials { font-size: 13px; }

.emp-body {
  text-align: center; flex: 1; width: 100%;
  display: flex; flex-direction: column; align-items: center;
}
.emp-name {
  font-size: 15px; font-weight: 700; color: #0a1628;
  margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; max-width: 100%;
}
.emp-email {
  font-size: 12px; color: #888; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; max-width: 100%;
}
.emp-position {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: #666; margin-top: 8px; font-weight: 500;
}

.emp-badges {
  display: flex; flex-wrap: wrap; gap: 4px; justify-content: center;
  margin-top: 12px; padding-top: 12px; border-top: 1px solid #f5f5f5; width: 100%;
}
.badge-role {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 3px 8px; border-radius: 12px;
  font-size: 10px; font-weight: 700;
}
.badge-tech { background: rgba(91,91,214,0.1); color: #5B5BD6; }
.badge-visual { background: rgba(46,125,50,0.1); color: #2E7D32; }

.emp-actions {
  position: absolute; top: 12px; right: 12px;
  display: flex; gap: 2px;
  opacity: 0; transition: opacity 0.15s;
}
.emp-card:hover .emp-actions { opacity: 1; }

/* List */
.list-wrap { background: white; border: 1px solid #eceef2; border-radius: 14px; overflow: hidden; }
.list-header {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) 90px;
  align-items: center; gap: 16px; padding: 12px 20px;
  background: #fafbfc; border-bottom: 1px solid #eceef2;
  font-size: 11px; font-weight: 700; color: #999;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.list-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) 90px;
  align-items: center; gap: 16px; padding: 12px 20px;
  border-bottom: 1px solid #f5f5f5; cursor: pointer;
  transition: background 0.15s;
}
.list-row:last-child { border-bottom: none; }
.list-row:hover { background: #fafbfc; }
.list-col-ava { display: flex; align-items: center; }
.list-name { font-size: 13.5px; font-weight: 600; color: #0a1628; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-muted { font-size: 12.5px; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-roles { display: flex; gap: 4px; flex-wrap: wrap; }
.list-col-actions { display: flex; gap: 4px; justify-content: flex-end; }

/* Empty */
.empty-state {
  background: white; border: 1px solid #eceef2; border-radius: 16px;
  padding: 64px 32px; text-align: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; }

/* Skeleton */
.loading-state { display: flex; flex-direction: column; gap: 12px; }
.skel-card-grid, .skel-card-list {
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 14px;
}
.skel-card-grid { height: 220px; }
.skel-card-list { height: 60px; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

@media (max-width: 900px) {
  .list-header, .list-row { grid-template-columns: 44px minmax(0, 1fr) minmax(0, 1fr) 80px; }
  .list-header > *:nth-child(4), .list-header > *:nth-child(5),
  .list-row > *:nth-child(4), .list-row > *:nth-child(5) { display: none; }
}
</style>
